// CoverageMapPanel — Decision Surface plan, Stage 1b (live state).
//
// Renders the requirements document's section map with exporter-true,
// forecast-labelled state: which sections will render content, which
// are empty, and which required sections will print "TBD". State comes
// from the backend's resolve_structure (the exporter's OWN walk run in
// no-emit mode), so the map can't claim a placement the exporter won't
// produce.
//
// Honesty rules:
//  * Node labels are the document's real headings, verbatim (D4) —
//    plain language is a subtitle only.
//  * Verbs are driven by `basis`: at decision time it's a FORECAST
//    ("will render / will print TBD") from the current Phase-3
//    artifacts; only a completed export reads as "authoritative".
//  * The compliance lens only claims a profile "drives" sections when
//    that flag actually has a structured profile (`profiled`).
//
// Hides itself on 404 (the `coverage_map_enabled` flag is off).

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Loader2,
  Map as MapIcon,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react'

import { getControlsCoverageFindings } from '@/lib/controlsCoverageFindingsApi'
import { getDataDictionaryFindings } from '@/lib/dataDictionaryFindingsApi'
import {
  DocumentStructureError,
  getDocumentStructure,
} from '@/lib/documentStructureApi'
import {
  summarizeFindingsBySection,
  type NodeFindingSummary,
  type NormalizedFinding,
} from '@/lib/findingSectionMap'
import { getThreatModelFindings } from '@/lib/threatModelFindingsApi'
import {
  SECTION_PLAIN_LANGUAGE,
  type DocumentStructureResponse,
  type SectionStateKind,
} from '@/types/documentStructure'

export interface CoverageMapPanelProps {
  projectId: string
  runId: number
}

function stateChip(
  state: SectionStateKind,
  forecast: boolean,
): { label: string; cls: string; Icon: typeof CheckCircle2 } {
  switch (state) {
    case 'filled':
      return {
        label: forecast ? 'Will render' : 'Rendered',
        cls: 'border-emerald-800 bg-emerald-950 text-emerald-100',
        Icon: CheckCircle2,
      }
    case 'empty_required':
      return {
        label: forecast ? 'Will print TBD' : 'TBD',
        cls: 'border-amber-700 bg-amber-950 text-amber-100',
        Icon: AlertTriangle,
      }
    case 'empty_optional':
    default:
      return {
        label: forecast ? "Won't be included" : 'Omitted',
        cls: 'border-slate-600 text-slate-300',
        Icon: Circle,
      }
  }
}

/**
 * The finding-impact chip on a node (Stage 2): what the open findings
 * landing here do to the export. Text + opaque bg on the SAME element so
 * axe-core can read the contrast (no nested recolored spans — the
 * Tailwind-v4 oklch-ancestor compositing limitation).
 */
function blockingChip(
  sum: NodeFindingSummary,
): { label: string; cls: string; Icon: typeof ShieldAlert } | null {
  if (sum.worst === 'blocks') {
    return {
      label: `${sum.blocks} block${sum.blocks === 1 ? 's' : ''} export`,
      cls: 'border-red-800 bg-red-950 text-red-100',
      Icon: ShieldAlert,
    }
  }
  if (sum.worst === 'deferrable-as-TBD') {
    return {
      label: `${sum.tbd} will print TBD`,
      cls: 'border-amber-800 bg-amber-950 text-amber-100',
      Icon: AlertTriangle,
    }
  }
  return null
}

/** §9-family ``target_content`` tokens a regulatory profile drives —
 *  used to count "driving N sections" in the compliance lens (Stage 3). */
const COMPLIANCE_FAMILY_TOKENS = new Set<string>([
  'compliance_risks',
  'data_classification_matrix',
  'threat_model',
  'asvs_mapping',
  'control_matrix',
  'risk_register',
  'data_dictionary_appendix',
])

export function CoverageMapPanel({ projectId, runId }: CoverageMapPanelProps) {
  const { data, isLoading, error } = useQuery<DocumentStructureResponse>({
    queryKey: ['document-structure', projectId, runId],
    queryFn: () => getDocumentStructure(projectId, runId),
    retry: false,
  })

  // Findings that land on the map's nodes (Stage 2). Fail-soft: each
  // source is optional + best-effort — an error / flag-off endpoint just
  // means that source contributes no annotations, never breaks the map.
  // Only fetched once the structure resolves (the map is what they
  // annotate).
  const ddFindings = useQuery({
    queryKey: ['dd-findings', projectId, runId],
    queryFn: () => getDataDictionaryFindings(projectId, runId),
    retry: false,
    enabled: Boolean(data),
  })
  const tmFindings = useQuery({
    queryKey: ['tm-findings', projectId, runId],
    queryFn: () => getThreatModelFindings(projectId, runId),
    retry: false,
    enabled: Boolean(data),
  })
  const ccFindings = useQuery({
    queryKey: ['cc-findings', projectId, runId],
    queryFn: () => getControlsCoverageFindings(projectId, runId),
    retry: false,
    enabled: Boolean(data),
  })

  const findingSummary = useMemo(() => {
    if (!data) return new Map<string, NodeFindingSummary>()
    const all: NormalizedFinding[] = [
      ...(ddFindings.data?.findings ?? []),
      ...(tmFindings.data?.findings ?? []),
      ...(ccFindings.data?.findings ?? []),
    ].map((f) => ({
      finding_kind: f.finding_kind,
      severity: f.severity,
      waived: f.waived,
    }))
    return summarizeFindingsBySection(data.sections, all)
  }, [data, ddFindings.data, tmFindings.data, ccFindings.data])

  // 404 → route not mounted (flag off) or run absent: hide entirely.
  if (error instanceof DocumentStructureError && error.status === 404) {
    return null
  }

  if (isLoading) {
    return (
      <div
        data-testid="coverage-map-panel"
        className="rounded-lg border border-slate-700/60 bg-slate-900 p-3"
      >
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading document structure…
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div
        data-testid="coverage-map-panel"
        className="rounded-lg border border-slate-700/60 bg-slate-900 p-3 text-sm text-slate-300"
      >
        Couldn't load the document structure.
      </div>
    )
  }

  const total = data.sections.length
  const filled = data.sections.filter((s) => s.state === 'filled').length
  const tbd = data.sections.filter((s) => s.state === 'empty_required').length
  const forecast = data.basis !== 'authoritative'
  const compliance = data.compliance
  // Stage 3 compliance lens: the §9-family sections a regulatory profile
  // drives, counted from THIS document's section set (honest — only the
  // sections actually present). Only meaningful when a profile is in play.
  const drivenSections = compliance.profiled
    ? data.sections.filter((s) =>
        (s.target_content ?? []).some((t) => COMPLIANCE_FAMILY_TOKENS.has(t)),
      ).length
    : 0

  return (
    <div
      data-testid="coverage-map-panel"
      className="rounded-lg border border-slate-700/60 bg-slate-900 p-3"
    >
      {/* Header */}
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-200">
            <MapIcon className="h-4 w-4 text-indigo-400" />
            Coverage Map
            <span
              data-testid="coverage-map-basis"
              className="rounded border border-slate-700 bg-slate-950 px-1.5 py-0.5 text-[10px] font-normal text-slate-400"
              title={
                forecast
                  ? 'A forecast from the current Phase-3 artifacts — the final document is assembled at export.'
                  : 'Reflects the completed export on disk.'
              }
            >
              {forecast ? 'forecast' : 'as exported'}
            </span>
          </h3>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 text-[11px] text-slate-400">
          <span data-testid="coverage-map-count">
            {total} {total === 1 ? 'section' : 'sections'}
          </span>
          <span className="rounded border border-slate-700/60 px-1.5 py-0.5 text-[10.5px] text-slate-300">
            {data.template.label}
          </span>
        </div>
      </div>

      {/* Rollup */}
      <div
        data-testid="coverage-map-rollup"
        className="mb-2 flex flex-wrap items-center gap-2 text-[11px]"
      >
        {/* Each stat carries its own opaque background so contrast is
            deterministic (axe-core can't composite a Tailwind-v4 oklch
            ancestor background; text + bg on the same element parses). */}
        <span className="inline-flex items-center gap-1 rounded border border-emerald-900 bg-emerald-950 px-1.5 py-0.5 text-emerald-200">
          <CheckCircle2 className="h-3 w-3" />
          {filled} ready
        </span>
        {tbd > 0 && (
          <span className="inline-flex items-center gap-1 rounded border border-amber-900 bg-amber-950 px-1.5 py-0.5 text-amber-100">
            <AlertTriangle className="h-3 w-3" />
            {tbd} {forecast ? 'will print TBD' : 'TBD'}
          </span>
        )}
      </div>

      {/* Cost of TBD (Stage 3) — the consequence of exporting as-is, in
          the architect's terms (forecast only; once exported it's moot). */}
      {forecast && tbd > 0 && (
        <div
          data-testid="coverage-map-tbd-cost"
          className="mb-2 rounded border border-amber-900/50 bg-amber-950/30 px-2 py-1 text-[11px] text-amber-200/90"
        >
          Export now and {tbd} required section{tbd === 1 ? '' : 's'} will print{' '}
          <strong>“TBD”</strong> instead of content.
        </div>
      )}

      {/* Compliance lens */}
      {compliance.profiles.length > 0 && (
        <div className="mb-2 flex items-start gap-1.5 rounded border border-slate-800 bg-slate-950 p-2 text-[11px] text-slate-300">
          <ShieldCheck className="mt-0.5 h-3 w-3 shrink-0 text-indigo-400" />
          <span>
            {/* Emphasis via font-weight, not a new text color — a nested
                recolored span without its own bg trips the same axe
                oklch-ancestor compositing limitation. */}
            {compliance.profiled ? (
              <>
                <span className="font-semibold">
                  {compliance.profiles.join(', ')}
                </span>{' '}
                is driving {drivenSections} section
                {drivenSections === 1 ? '' : 's'}
              </>
            ) : (
              <span className="font-semibold">
                {compliance.profiles.join(', ')}
              </span>
            )}
            {compliance.injected_requirement_count > 0 && (
              <>
                {' '}
                · {compliance.injected_requirement_count} requirement
                {compliance.injected_requirement_count === 1 ? '' : 's'} injected
              </>
            )}
          </span>
        </div>
      )}

      {/* Section outline */}
      <ol className="flex flex-col gap-1.5">
        {data.sections.map((s) => {
          const subtitle = SECTION_PLAIN_LANGUAGE[s.id]
          const chip = stateChip(s.state, forecast)
          const summary = findingSummary.get(s.id)
          const fchip = summary ? blockingChip(summary) : null
          return (
            <li
              key={s.id}
              data-testid="coverage-map-section"
              data-section-id={s.id}
              data-state={s.state}
              data-blocking-class={summary?.worst ?? 'none'}
              className="rounded border border-slate-800 bg-slate-950 px-2.5 py-1.5"
            >
              <div className="flex items-center gap-2">
                {/* Real heading, verbatim (D4). */}
                <span className="min-w-0 flex-1 truncate text-sm text-slate-100">
                  {s.heading}
                </span>
                {fchip && (
                  <span
                    data-testid="coverage-map-finding-chip"
                    title={`${summary?.total} open finding${summary?.total === 1 ? '' : 's'} land here`}
                    className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${fchip.cls}`}
                  >
                    <fchip.Icon className="h-2.5 w-2.5" />
                    {fchip.label}
                  </span>
                )}
                <span
                  className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${chip.cls}`}
                >
                  <chip.Icon className="h-2.5 w-2.5" />
                  {chip.label}
                </span>
              </div>
              {subtitle && (
                <div className="mt-0.5 text-[11px] text-slate-400">
                  {subtitle}
                </div>
              )}
            </li>
          )
        })}
      </ol>

      {/* Orphan content (Stage 3.5) — generated artifacts that have
          content but no section in the resolved template to hold them,
          so they won't appear in the document. */}
      {data.orphan_content.length > 0 && (
        <div
          data-testid="coverage-map-orphans"
          className="mt-2 rounded border border-slate-700/60 bg-slate-950 p-2 text-[11px] text-slate-300"
        >
          <div className="flex items-center gap-1.5 text-slate-200">
            <Circle className="h-3 w-3 text-slate-400" />
            {data.orphan_content.length} generated{' '}
            {data.orphan_content.length === 1 ? 'artifact' : 'artifacts'} won’t
            appear in this document
          </div>
          <div className="mt-0.5 text-slate-400">
            This template has no section to place{' '}
            {data.orphan_content.length === 1 ? 'it' : 'them'}:{' '}
            {data.orphan_content
              .map((t) => t.replace(/_/g, ' '))
              .join(', ')}
            .
          </div>
        </div>
      )}
    </div>
  )
}
